// Military content rendered INSIDE the sidebar (no floating widget over the
// map): unit category layers, drag-to-deploy list (units checked
// "déployable" in the ORBAT), and the "Plan de guerre" tools — layers
// (calques), draw tools with diagram-like style options (arrow heads, line
// width, dashes, color), free symbol placement from the saved symbols.

import { useEffect, useRef, useState } from 'react'
import { unitSymbolSvg, UNIT_CATEGORIES } from '../utils/military'
import Icon from './Icons'

const sectionTitle = {
  margin: '12px 0 6px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '.06em', color: 'var(--text-secondary, #808080)',
  borderBottom: '1px solid var(--border-color, #2a2a2a)', paddingBottom: 4,
}

const btn = {
  background: 'var(--background-light, #1e1e1e)', color: 'var(--text-primary, #f0f0f0)',
  border: '1px solid var(--border-color, #2a2a2a)',
  borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
}

const hint = { color: 'var(--text-secondary, #808080)', margin: 0, fontSize: 12 }

const MilitaryPanel = ({
  categories, onToggleCategory,
  undeployedUnits, ownedCountryId,
  plans, activePlan, planTool, readOnlyPlan,
  onSelectPlan, onCreatePlan, onDeletePlan, onSharePlan, onClosePlan,
  onSetPlanTool, onUndoFeature, onSavePlan, planDirty,
  planningMode, planningOpen,
  deployHighlightId,
  planLayers, activeLayer, hiddenLayers,
  onSelectLayer, onAddLayer, onToggleLayerVisibility,
  planColor, onSetPlanColor,
  planStyle, onSetPlanStyle,
  mySymbols, planSymbol, onSetPlanSymbol,
}) => {
  const [showAllUnits, setShowAllUnits] = useState(false)
  const highlightRef = useRef(null)

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [deployHighlightId, undeployedUnits.length])

  const listedUnits = showAllUnits
    ? undeployedUnits
    : undeployedUnits.filter((u) => u.is_deployable || u.id === deployHighlightId)

  const setStyleField = (field, value) => onSetPlanStyle({ ...planStyle, [field]: value })

  const styleBtn = (active) => ({
    ...btn,
    padding: '3px 7px',
    borderColor: active ? '#D5B654' : 'var(--border-color, #2a2a2a)',
    color: active ? '#D5B654' : undefined,
  })

  return (
    <>
      <div style={sectionTitle}>Calques d'unités</div>
      <div className="mil-category-grid">
        {UNIT_CATEGORIES.map((c) => (
          <label key={c.key} className="mil-category-item">
            <input
              type="checkbox" className="pr-check"
              checked={categories[c.key] !== false}
              onChange={() => onToggleCategory(c.key)}
            />
            <span>{c.label}</span>
          </label>
        ))}
      </div>

      {ownedCountryId && (
        <>
          <div style={sectionTitle}>{planningMode ? 'Mes unités (glisser sur le plan)' : 'Mes unités à déployer'}</div>
          <label className="mil-category-item" style={{ marginBottom: 6, ...hint }}>
            <input
              type="checkbox" className="pr-check"
              checked={showAllUnits}
              onChange={() => setShowAllUnits((v) => !v)}
            />
            <span>Toutes les unités (défaut : cochées « déployable »)</span>
          </label>
          {listedUnits.length === 0 && (
            <p style={hint}>
              {undeployedUnits.length === 0
                ? 'Toutes tes unités sont déployées.'
                : 'Aucune unité marquée « déployable » — coche-les dans l\'ORBAT (Commandement militaire).'}
            </p>
          )}
          {listedUnits.map((u) => {
            const highlighted = u.id === deployHighlightId
            return (
              <div
                key={u.id}
                ref={highlighted ? highlightRef : null}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/mil-deploy-unit', String(u.id))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px',
                  background: highlighted ? 'rgba(213,182,84,.15)' : 'var(--background-light, #1e1e1e)',
                  border: highlighted ? '1px solid #D5B654' : '1px solid transparent',
                  borderRadius: 6, marginBottom: 3, cursor: 'grab',
                }}
                title={planningMode ? 'Glisser sur la carte pour placer dans le plan' : 'Glisser sur la carte pour déployer'}
              >
                <span dangerouslySetInnerHTML={{ __html: unitSymbolSvg(u, 20) }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                {u.echelon?.name ? (
                  <span style={{ ...hint, fontSize: 11, whiteSpace: 'nowrap' }}>{u.echelon.name}</span>
                ) : null}
              </div>
            )
          })}
        </>
      )}

      {(planningOpen || readOnlyPlan) && (
        <>
          <div style={sectionTitle}>Plan de guerre</div>
          {readOnlyPlan ? (
            <p style={{ color: '#e0b050', margin: 0, fontSize: 12 }}>
              Plan partagé (lecture seule) : <strong>{activePlan?.name}</strong>
            </p>
          ) : activePlan ? (
            <>
              <p style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '0 0 6px' }}>
                <Icon name="edit" size={13} /> <strong>{activePlan.name}</strong>{planDirty ? ' *' : ''}
              </p>
              <p style={{ ...hint, marginBottom: 6, fontSize: 11 }}>
                Unités placées dans le plan : masquées de la carte ; les autres
                apparaissent en transparence.
              </p>

              <div style={{ ...sectionTitle, marginTop: 8 }}>Calques</div>
              {(planLayers || []).map((name) => (
                <div key={name} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                  <input
                    type="checkbox" className="pr-check"
                    title="Afficher / masquer"
                    checked={!(hiddenLayers || []).includes(name)}
                    onChange={() => onToggleLayerVisibility(name)}
                  />
                  <button
                    style={{
                      ...btn, flex: 1, textAlign: 'left',
                      borderColor: activeLayer === name ? '#D5B654' : 'var(--border-color, #2a2a2a)',
                      color: activeLayer === name ? '#D5B654' : undefined,
                    }}
                    title="Dessiner sur ce calque"
                    onClick={() => onSelectLayer(name)}
                  >{name}</button>
                </div>
              ))}
              <button style={{ ...btn, width: '100%', marginBottom: 8 }} onClick={onAddLayer}>+ Calque</button>

              <div style={{ ...sectionTitle, marginTop: 8 }}>Outils</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
                {[['symbol', 'Symbole'], ['arrow', 'Flèche'], ['line', 'Ligne'], ['polygon', 'Zone'], ['label', 'Texte'], ['erase', 'Effacer']].map(([t, l]) => (
                  <button
                    key={t}
                    style={styleBtn(planTool === t)}
                    onClick={() => onSetPlanTool(planTool === t ? null : t)}
                  >{l}</button>
                ))}
              </div>

              {/* Style du trait (façon diagrams.net) */}
              {(planTool === 'arrow' || planTool === 'line' || planTool === 'polygon' || planTool === 'label') && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={planColor || '#e07070'}
                    onChange={(e) => onSetPlanColor(e.target.value)}
                    title="Couleur"
                    style={{ width: 28, height: 24, padding: 0, border: '1px solid #2a2a2a', borderRadius: 4, background: 'none', cursor: 'pointer' }}
                  />
                  {planTool !== 'label' && (
                    <>
                      {[['thin', '─ fin'], ['normal', '━ normal'], ['thick', '▬ épais']].map(([w, l]) => (
                        <button key={w} style={styleBtn((planStyle.width || 'normal') === w)} onClick={() => setStyleField('width', w)}>{l}</button>
                      ))}
                      {[['solid', 'plein'], ['dash', '╌ tirets'], ['dot', '⋯ points']].map(([d, l]) => (
                        <button key={d} style={styleBtn((planStyle.dash || 'solid') === d)} onClick={() => setStyleField('dash', d)}>{l}</button>
                      ))}
                    </>
                  )}
                  {planTool === 'arrow' && (
                    <>
                      {[[0, '— sans'], [1, '→ 1 pointe'], [2, '↔ 2 pointes']].map(([h, l]) => (
                        <button key={h} style={styleBtn((planStyle.heads ?? 1) === h)} onClick={() => setStyleField('heads', h)}>{l}</button>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Choix du symbole libre */}
              {planTool === 'symbol' && (
                <div style={{ marginBottom: 6 }}>
                  <p style={{ ...hint, marginBottom: 4 }}>Symbole à placer (clic sur la carte) :</p>
                  <div style={{ maxHeight: 150, overflowY: 'auto', marginBottom: 4 }}>
                    {(mySymbols || []).map((s) => (
                      <div
                        key={s.id}
                        onClick={() => onSetPlanSymbol(s.sidc)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px',
                          borderRadius: 4, cursor: 'pointer',
                          border: planSymbol === s.sidc ? '1px solid #D5B654' : '1px solid transparent',
                          background: 'var(--background-light, #1e1e1e)', marginBottom: 2,
                        }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: s.svg || '' }} />
                        <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      </div>
                    ))}
                    {!(mySymbols || []).length && (
                      <p style={hint}>Aucun symbole enregistré — crée-les dans le Concepteur (onglet Symboles).</p>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="…ou SIDC libre (20 chiffres)"
                    defaultValue={planSymbol && !(mySymbols || []).some((s) => s.sidc === planSymbol) ? planSymbol : ''}
                    onChange={(e) => { if (e.target.value.length === 20) onSetPlanSymbol(e.target.value) }}
                    style={{ width: '100%', background: 'var(--background-light, #1e1e1e)', color: 'inherit', border: '1px solid var(--border-color, #2a2a2a)', borderRadius: 4, padding: '4px 6px', fontSize: 12 }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <button style={{ ...btn, display: 'flex', alignItems: 'center', gap: 5 }} onClick={onUndoFeature}><Icon name="undo" size={13} /> Annuler</button>
                <button style={{ ...btn, background: '#2d4a2d', display: 'flex', alignItems: 'center', gap: 5 }} onClick={onSavePlan}><Icon name="save" size={13} /> Sauver</button>
                <button style={{ ...btn, display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => onSharePlan(activePlan)}><Icon name="link" size={13} /> Partager</button>
                <button style={btn} onClick={onClosePlan}>Fermer</button>
              </div>
              {planTool === 'line' || planTool === 'polygon' || planTool === 'arrow' ? (
                <p style={{ ...hint, margin: '6px 0 0' }}>
                  Clique pour ajouter des points, re-sélectionne l'outil pour terminer le tracé.
                </p>
              ) : null}
            </>
          ) : (
            <>
              {plans.map((p) => (
                <div key={p.id} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
                  <button style={{ ...btn, flex: 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => onSelectPlan(p)}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    {p.share_token && <Icon name="link" size={12} />}
                  </button>
                  <button style={{ ...btn, display: 'flex' }} title="Supprimer" onClick={() => onDeletePlan(p)}><Icon name="trash" size={13} /></button>
                </div>
              ))}
              {ownedCountryId ? (
                <button style={{ ...btn, width: '100%', marginTop: 4 }} onClick={onCreatePlan}>+ Nouveau plan</button>
              ) : (
                <p style={hint}>Connecte-toi pour créer des plans.</p>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

export default MilitaryPanel
